import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { DEFAULT_LOCALE, isLocale } from "@/config/site";
import { messagesByLocale } from "@/i18n/catalog";
import {
  I18nContext,
  type I18nContextValue,
} from "@/i18n/I18nContext";
import type { Locale } from "@/i18n/types";

const STORAGE_KEY = "wasok.locale";

function readLocaleFromPath(): Locale | null {
  if (typeof window === "undefined") return null;
  const segment = window.location.pathname.split("/").filter(Boolean)[0];
  if (segment && isLocale(segment)) return segment;
  return null;
}

function readInitialLocale(): Locale {
  const fromPath = readLocaleFromPath();
  if (fromPath) return fromPath;
  return DEFAULT_LOCALE;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readInitialLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const messages = messagesByLocale[locale];

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, messages }),
    [locale, setLocale, messages],
  );

  return (
    <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
  );
}
